import { ReactElement, useEffect, useState } from "react";
import cn from "classnames";
import { graphql } from "gatsby";
import { StaticImage } from "gatsby-plugin-image";
import { useI18next, useTranslation } from "gatsby-plugin-react-i18next";
import parse from "html-react-parser";

import Icons from "@core/ui/images/icomoon/symbol-defs.svg";
import { useCountry } from "@func-core/core/application/hooks/useCountry";
import { CountryCode, Locale } from "@settings/countries";
import useAxeptioScriptLoader from "@utils/hooks/useAxeptioScriptLoader";

import BackgroundShape from "@atoms/background-shape/BackgroundShape";
import ChatLink from "@atoms/chat-link/ChatLink";
import CountryChangeSuggester from "@molecules/country-change-suggester/CountryChangeSuggester";
import AppReimbursement from "@organisms/app-reimbursement/app_reimbursement";
import CompanyValues from "@organisms/company-values/CompanyValues";
import PreSalesFaqs from "@organisms/faqs/PreSalesFaqs";
import PreSalesFooter from "@organisms/footer/PreSalesFooter";
import GoogleReviews from "@organisms/google-reviews/GoogleReviews";
import Hero from "@organisms/hero/Hero";
import KspList from "@organisms/ksp-list/KspList";
import LandingBottomCta from "@organisms/landing-bottom-cta/LandingBottomCta";
import Meta from "@organisms/meta/meta";
import Navigation from "@organisms/navigation/Navigation";
import PageWrapper from "@organisms/page-wrapper/PageWrapper";
import Reimbursement from "@organisms/reimbursement/reimbursement";
import { useLanding } from "@templates/pre-sales-landing/useLanding";

import * as styles from "@templates/pre-sales-landing/PreSalesLandingPage.module.scss";

interface PreSalesLandingPageProps {
  pageContext: {
    locale: string;
    language: string;
    defaultLanguage: string;
  };
  location: Location | undefined;
}

const PreSalesLandingPage = ({ pageContext, location }: PreSalesLandingPageProps): ReactElement => {
  const { t } = useTranslation();
  const { country } = useCountry();
  const {
    kspList,
    benefits,
    reimbursement,
    showCompanyValuesSection,
    showCommittedToAnimalsSection,
    showAssociationsSection,
    appReimbursement,
    faqs,
    normalizedCountryCode,
    faqItems,
    hasAlternativeBenefitsSection,
    reviewsWidget,
    landingBottomCta,
    showChatLink,
    alternateVersions,
    footerConfig,
  } = useLanding();
  const [isLanguageReady, setIsLanguageReady] = useState(false);

  const { i18n } = useI18next();

  useEffect(() => {
    const { language, changeLanguage } = i18n;

    const initializeLanguage = async () => {
      if (isLanguageReady) {
        return;
      }

      if (language === pageContext.locale) {
        setIsLanguageReady(true);
      } else {
        await changeLanguage(pageContext.locale);
        setIsLanguageReady(true);
      }
    };

    void initializeLanguage();
  }, [i18n, isLanguageReady, pageContext.locale]);

  useAxeptioScriptLoader(country.language.code.split("-")[0].toLowerCase());

  return (
    <>
      <Meta
        title={t("landing.meta.title")}
        description={t("landing.meta.description")}
        slug={"/"}
        alternateVersions={alternateVersions}
        lang={pageContext.language as Locale}
        bodyClass={styles.landing}
        disableTranslations={false}
      />

      {isLanguageReady && (
        <PageWrapper>
          <Navigation
            location={location}
            countryCodeIso={normalizedCountryCode}
            adoptionClassName={styles.navigation}
          />
          <main className={cn(styles.main, "or-main")}>
            <CountryChangeSuggester
              countryCodeIso={normalizedCountryCode}
              adoptionClassName={styles.countryChangeSuggester}
            />

            <Hero
              data={{
                title: t("landing.hero.title"),
                list: [
                  t("landing.hero.list.item_1"),
                  t("landing.hero.list.item_2"),
                  t("landing.hero.list.item_3", { country: country.name }),
                ],
                shouldShowPetSpeciesSelector: true,
                reviewsLink: `https://${country.iso.toLocaleLowerCase()}.trustpilot.com/review/barkibu.com`,
              }}
              countryCodeIso={normalizedCountryCode}
              adoptionClassName={styles.hero}
            />

            {benefits && (
              <KspList
                data={{ title: t("landing.ksp_list.title"), ksps: kspList }}
                variantName={hasAlternativeBenefitsSection ? "alternativeBenefits" : "benefits"}
                adoptionClassName={styles.kspList}
              />
            )}

            {reimbursement && (
              <Reimbursement
                data={{
                  title: t("landing.reimbursement.title"),
                  subtitle: t("landing.reimbursement.subtitle"),
                }}
                adoptionClassName={styles.reimbursement}
              />
            )}

            {showCompanyValuesSection && (
              <CompanyValues
                variantName={"designedByPetParents"}
                title={parse(t("landing.designed_by_petparents.title"))}
                subtitle={t("landing.designed_by_petparents.subtitle")}
              >
                <div>
                  <figure>
                    <BackgroundShape variantName="companyValues" />
                    <StaticImage
                      placeholder="blurred"
                      src={"../../images/organisms/company-values/barkibu-dogs-and-humans.jpg"}
                      alt={t("landing.designed_by_petparents.image.1")}
                    />
                    <StaticImage
                      placeholder="blurred"
                      src={"../../images/organisms/company-values/videocall-with-vet.jpg"}
                      alt={t("landing.designed_by_petparents.image.2")}
                    />
                  </figure>
                  <p>{parse(t("landing.designed_by_petparents.description.paragraphs.1"))}</p>
                  <p>{parse(t("landing.designed_by_petparents.description.paragraphs.2"))}</p>
                  <ul>
                    {[1, 2, 3, 4].map((i) => (
                      <li key={i}>
                        {parse(t(`landing.designed_by_petparents.description.list_items.${i}`))}
                      </li>
                    ))}
                  </ul>
                </div>
              </CompanyValues>
            )}

            {showCommittedToAnimalsSection && (
              <CompanyValues
                title={parse(t("landing.committed_to_animals.title"))}
                subtitle={t("landing.committed_to_animals.subtitle")}
                variantName={"committedToAnimals"}
              >
                <ul>
                  <li>
                    <article>
                      <StaticImage
                        placeholder="blurred"
                        src="../../images/organisms/company-values/patri_chochi.jpg"
                        alt=""
                      />
                      <h3>{parse(t("landing.committed_to_animals.1.title"))}</h3>
                      <p>{parse(t("landing.committed_to_animals.1.description"))}</p>
                    </article>
                  </li>
                  <li>
                    <article>
                      <StaticImage
                        placeholder="blurred"
                        src="../../images/organisms/company-values/marta_viejito.jpg"
                        alt=""
                      />
                      <h3>{parse(t("landing.committed_to_animals.2.title"))}</h3>
                      <p>{parse(t("landing.committed_to_animals.2.description"))}</p>
                    </article>
                  </li>
                </ul>
              </CompanyValues>
            )}

            {showAssociationsSection && (
              <CompanyValues
                title={parse(t("landing.social_causes.title"))}
                subtitle={t("landing.social_causes.subtitle")}
                description={parse(t("landing.social_causes.description"))}
                variantName={"socialCauses"}
              >
                <ul>
                  <li>
                    <StaticImage
                      placeholder="blurred"
                      className="social-causes-logo-advocate"
                      src="../../images/organisms/company-values/nailted_badge.svg"
                      alt={t("landing.social_causes.nailted.alt")}
                    />
                  </li>
                  <li>
                    <StaticImage
                      placeholder="blurred"
                      className="social-causes-logo-nailted"
                      src="../../images/organisms/company-values/nailted.png"
                      alt={t("landing.social_causes.nailted.alt")}
                    />
                  </li>
                  <li>
                    <StaticImage
                      placeholder="blurred"
                      className="social-causes-logo-escan"
                      src="../../images/organisms/company-values/escan.jpg"
                      alt={t("landing.social_causes.escan.alt")}
                    />
                  </li>
                  <li>
                    <StaticImage
                      placeholder="blurred"
                      className="social-causes-logo-anaa"
                      src="../../images/organisms/company-values/anaa.jpg"
                      alt={t("landing.social_causes.anaa.alt")}
                    />
                  </li>
                </ul>
              </CompanyValues>
            )}

            {appReimbursement && (
              <AppReimbursement
                data={{
                  title: t("landing.app_reimbursement.title"),
                  subtitle: t("landing.app_reimbursement.subtitle"),
                }}
                adoptionClassName={styles.appReimbursement}
              />
            )}

            {faqs && <PreSalesFaqs title={t("landing.faqs.title")} faqItems={faqItems} />}

            {reviewsWidget && (
              <GoogleReviews
                data={{ title: t(reviewsWidget.title), widgetId: reviewsWidget.widgetId }}
              />
            )}

            {landingBottomCta && (
              <LandingBottomCta
                data={{
                  cta: t("landing.bottom_cta.title"),
                  subtitle: t("landing.bottom_cta.subtitle"),
                }}
                countryCodeIso={normalizedCountryCode}
                adoptionClassName={styles.homeBottomCta}
              />
            )}
          </main>

          <PreSalesFooter
            adoptionClassName={styles.footer}
            isPreSales={false}
            footerConfig={footerConfig}
          />
          <BackgroundShape adoptionClassName={styles.backgroundShape} />
          <Icons />
          {showChatLink && <ChatLink countryCodeIso={country.iso as CountryCode} />}
        </PageWrapper>
      )}
    </>
  );
};

export default PreSalesLandingPage;
export const query = graphql`
  query ($language: String!) {
    locales: allLocale(filter: { language: { eq: $language } }) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
  }
`;
